import { useState, useEffect, useCallback } from 'react';
import PlatformValueTab from './PlatformValueTab';
import AccountUsageScoreCard from './AccountUsageScoreCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  platformAnalyticsAPI,
  type PlatformOverview,
  type FeatureUsageItem,
  type CompanyEngagement,
  type UserActivityItem,
  type TrendPoint,
  type ProcessAdoptionItem,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Users, Building2, Activity, TrendingUp, BarChart3, Zap } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Cell,
} from 'recharts';

const FEATURE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard', purchasing: 'Purchasing', quality: 'Quality',
  suppliers: 'Suppliers', documents: 'Documents', onboarding: 'Onboarding',
  forecasting: 'Forecasting', rfq: 'RFQ', calendar: 'Calendar',
  notifications: 'Notifications', news: 'News', reports: 'Reports',
  call_off: 'Call Off', drilldown: 'Drilldown', settings: 'Settings',
};

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
  '#ec4899', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7',
  '#e11d48', '#65a30d', '#0ea5e9',
];

const STATUS_COLORS = { high: '#10b981', medium: '#f59e0b', low: '#ef4444' };

export default function PlatformAnalytics() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [features, setFeatures] = useState<FeatureUsageItem[]>([]);
  const [companies, setCompanies] = useState<CompanyEngagement[]>([]);
  const [users, setUsers] = useState<UserActivityItem[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [adoption, setAdoption] = useState<ProcessAdoptionItem[]>([]);
  const [dateRange, setDateRange] = useState({ date_from: '', date_to: '' });
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState('30 Days');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      ...(dateRange.date_from && { date_from: dateRange.date_from }),
      ...(dateRange.date_to && { date_to: dateRange.date_to }),
      ...(companyTypeFilter && { company_type: companyTypeFilter }),
    };
    try {
      const [ov, fu, ce, ua, at, pa] = await Promise.all([
        platformAnalyticsAPI.getOverview(params),
        platformAnalyticsAPI.getFeatureUsage(params),
        platformAnalyticsAPI.getCompanyEngagement({ ...params, limit: 20 }),
        platformAnalyticsAPI.getUserActivity({ ...params, limit: 30 }),
        platformAnalyticsAPI.getActivityTrend(params),
        platformAnalyticsAPI.getProcessAdoption(params),
      ]);
      setOverview(ov.data as unknown as PlatformOverview);
      setFeatures((fu.data as unknown as { features: FeatureUsageItem[] }).features || []);
      setCompanies((ce.data as unknown as { companies: CompanyEngagement[] }).companies || []);
      setUsers((ua.data as unknown as { users: UserActivityItem[] }).users || []);
      setTrend((at.data as unknown as { trend: TrendPoint[] }).trend || []);
      setAdoption((pa.data as unknown as { adoption: ProcessAdoptionItem[] }).adoption || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load analytics';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [dateRange, companyTypeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading platform analytics…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive text-lg">{error}</p>
        <button onClick={fetchData} className="mt-4 text-primary underline">Retry</button>
      </div>
    );
  }

  const kpis = overview?.kpis;

  const quickDatePresets: { label: string; days: number }[] = [
    { label: '7 Days', days: 7 },
    { label: '30 Days', days: 30 },
    { label: '60 Days', days: 60 },
    { label: '90 Days', days: 90 },
  ];

  const applyPreset = (label: string, days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateRange({
      date_from: from.toISOString().split('T')[0],
      date_to: to.toISOString().split('T')[0],
    });
    setActivePreset(label);
  };

  const applyMonthPreset = (label: string, monthOffset: number) => {
    const now = new Date();
    const targetMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const monthEnd = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
    setDateRange({
      date_from: targetMonth.toISOString().split('T')[0],
      date_to: monthEnd.toISOString().split('T')[0],
    });
    setActivePreset(label);
  };

  const applyYearPreset = () => {
    const now = new Date();
    setDateRange({
      date_from: `${now.getFullYear()}-01-01`,
      date_to: now.toISOString().split('T')[0],
    });
    setActivePreset('This Year');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Platform Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Real use of Helix: usage scores, feature adoption and activity — background polling, Valuechain staff and Login-As kept apart
          </p>
        </div>
      </div>

      <Tabs defaultValue="usage">
        <TabsList>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="value">Platform Value</TabsTrigger>
        </TabsList>

        <TabsContent value="value" className="mt-4">
          <PlatformValueTab />
        </TabsContent>

        <TabsContent value="usage" className="mt-4 space-y-6">
      <AccountUsageScoreCard />

      <div className="flex items-center justify-end">
        <select
          value={companyTypeFilter}
          onChange={(e) => setCompanyTypeFilter(e.target.value)}
          className="px-3 py-2 border border-border rounded-md bg-background text-sm"
        >
          <option value="">All Company Types</option>
          <option value="customer">Customers</option>
          <option value="supplier">Suppliers</option>
        </select>
      </div>

      {/* Quick Date Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-muted-foreground mr-1">Quick Filters:</span>
        {quickDatePresets.map(({ label, days }) => (
          <button
            key={label}
            onClick={() => applyPreset(label, days)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              activePreset === label
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-foreground border-border hover:bg-accent'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => applyMonthPreset('This Month', 0)}
          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
            activePreset === 'This Month'
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-border hover:bg-accent'
          }`}
        >
          This Month
        </button>
        <button
          onClick={() => applyMonthPreset('Last Month', -1)}
          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
            activePreset === 'Last Month'
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-border hover:bg-accent'
          }`}
        >
          Last Month
        </button>
        <button
          onClick={applyYearPreset}
          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
            activePreset === 'This Year'
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-border hover:bg-accent'
          }`}
        >
          This Year
        </button>

        <div className="ml-auto flex items-center gap-2">
          <input
            type="date"
            value={dateRange.date_from}
            onChange={(e) => { setDateRange(prev => ({ ...prev, date_from: e.target.value })); setActivePreset(''); }}
            className="px-3 py-1.5 border border-border rounded-md bg-background text-sm"
          />
          <span className="text-muted-foreground text-sm">to</span>
          <input
            type="date"
            value={dateRange.date_to}
            onChange={(e) => { setDateRange(prev => ({ ...prev, date_to: e.target.value })); setActivePreset(''); }}
            className="px-3 py-1.5 border border-border rounded-md bg-background text-sm"
          />
        </div>
      </div>

      {overview?.excluded && (
        <p className="text-xs text-muted-foreground">
          Real use only. Left out of the figures below for this period, across all companies:{' '}
          {overview.excluded.staff_requests.toLocaleString()} requests by {overview.excluded.staff_users} Valuechain staff,{' '}
          {overview.excluded.login_as_requests.toLocaleString()} made under Login-As for {overview.excluded.login_as_users} people,{' '}
          and {overview.excluded.background_polls.toLocaleString()} background notification checks.
        </p>
      )}

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <KPICard icon={Users} label="Daily Active Users" value={kpis.dau} color="text-blue-600" />
          <KPICard icon={Users} label="Weekly Active Users" value={kpis.wau} color="text-indigo-600" />
          <KPICard icon={Users} label="Monthly Active Users" value={kpis.mau} color="text-purple-600" />
          <KPICard icon={Building2} label="Active Companies" value={kpis.active_companies}
            subtitle={`${kpis.active_customer_companies} customers · ${kpis.active_supplier_companies} suppliers`}
            color="text-emerald-600" />
          <KPICard icon={Zap} label="Stickiness (DAU/MAU)" value={`${kpis.stickiness_dau_mau}%`} color="text-amber-600" />
        </div>
      )}

      {/* Activity Trend Chart */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Activity Trend</CardTitle></CardHeader>
        <CardContent>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line yAxisId="left" type="monotone" dataKey="requests" stroke="#3b82f6" strokeWidth={2} name="Requests" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="unique_users" stroke="#10b981" strokeWidth={2} name="Unique Users" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground py-8">No trend data yet — usage tracking starts accumulating from today.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feature Usage */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Feature Usage</CardTitle></CardHeader>
          <CardContent>
            {features.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={features.map(f => ({ ...f, label: FEATURE_LABELS[f.feature] || f.feature }))} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="label" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="requests" name="Requests">
                    {features.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-8">No feature usage data yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Process Adoption */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> Process Adoption</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {adoption.map((a) => (
                <div key={a.feature} className="flex items-center gap-3">
                  <span className="text-sm w-24 text-right font-medium">{FEATURE_LABELS[a.feature] || a.feature}</span>
                  <div className="flex-1 bg-muted rounded-full h-6 relative overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(a.adoption_percentage, 2)}%`,
                        backgroundColor: STATUS_COLORS[a.status],
                      }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
                      {a.adoption_percentage}% ({a.companies_using}/{a.total_companies})
                    </span>
                  </div>
                </div>
              ))}
              {adoption.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No adoption data yet.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Usage Score */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> Company Usage Score</CardTitle>
            <p className="text-xs text-muted-foreground">40% how often people use it · 30% share of logins active · 30% work areas used · change vs the same-length period before</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Company</th>
                    <th className="pb-2 font-medium">Type</th>
                    <th className="pb-2 font-medium text-right" title="Active people / people with a login">Active</th>
                    <th className="pb-2 font-medium text-right" title="Work areas used">Areas</th>
                    <th className="pb-2 font-medium text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.company_id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-2 font-medium">{c.company_name}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.company_type === 'customer' ? 'bg-blue-100 text-blue-700' :
                          c.company_type === 'supplier' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {c.company_type}
                        </span>
                      </td>
                      <td className="py-2 text-right tabular-nums">{c.active_users}/{c.logins}</td>
                      <td className="py-2 text-right">{c.unique_features}</td>
                      <td className="py-2 text-right">
                        <span className="font-bold tabular-nums">{c.engagement_score}</span>
                        {c.change !== null && (
                          <span
                            className={`ml-1 text-xs tabular-nums ${c.change > 0 ? 'text-emerald-700' : c.change < 0 ? 'text-red-700' : 'text-muted-foreground'}`}
                            title={`Previous period: ${c.previous_score}`}
                          >
                            {c.change > 0 ? '+' : ''}{c.change}
                          </span>
                        )}
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          c.status === 'healthy' ? 'bg-emerald-100 text-emerald-800' :
                          c.status === 'watch' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                        }`}>{c.status === 'at_risk' ? 'at risk' : c.status}</span>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted-foreground py-8">No company data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* User Activity Leaderboard */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> User Activity</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Company</th>
                    <th className="pb-2 font-medium text-right">Requests</th>
                    <th className="pb-2 font-medium text-right">Features</th>
                    <th className="pb-2 font-medium text-right">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-2">
                        <div className="font-medium">{u.name || u.email}</div>
                        {u.name && <div className="text-xs text-muted-foreground">{u.email}</div>}
                      </td>
                      <td className="py-2 text-xs">{u.company}</td>
                      <td className="py-2 text-right font-mono">{u.total_requests}</td>
                      <td className="py-2 text-right">{u.unique_features}</td>
                      <td className="py-2 text-right">{u.active_days}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted-foreground py-8">No user data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, subtitle, color }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <Icon className={`h-5 w-5 ${color} opacity-60`} />
        </div>
      </CardContent>
    </Card>
  );
}
